// src/pages/messages/ChatRoomV2.jsx
//
// Chat v2 detail page. Real-time message thread with composer.
// Originally rolled out in phases (3: read-only, 4: text composer,
// 5: image attachments) — all shipped May 2026.
//
// What this page does:
//   - Sticky header: back button, chat name, type/member count
//   - Real-time message list (most recent 100 messages)
//   - Auto-scroll to bottom on first load + on new incoming message
//   - Auto mark-as-read on open + when new messages arrive
//   - Message bubble styling: my messages right (lime), others left
//     (white), system messages centered (zinc)
//   - Image messages: render thumbnail with click-to-open-fullsize
//   - Friendly empty state for never-posted-to channels
//
// Permissions are entirely Firestore-rule driven — if the user
// isn't in participantIds, the snapshot returns permission-denied
// and we render the not-found state.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { activeLocale } from '@/utils/formatLocale'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/context/AuthContext'
import { useRole } from '@/context/RoleContext'
import useChat from '@/hooks/useChat'
import useChatMessages from '@/hooks/useChatMessages'
import { markChatRead } from '@/services/chatReads'
import ChatComposerV2 from '@/components/chat/ChatComposerV2'
import MessageCoachBar from '@/components/chat/MessageCoachBar'
import TeamSubNav from '@/components/team/TeamSubNav'
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  UserIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

/* ---------- Helpers ----------------------------------------- */

function tsToMs(ts) {
  if (!ts) return 0
  if (typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts === 'number') return ts
  return 0
}

function fmtTime(ts) {
  const ms = tsToMs(ts)
  if (!ms) return ''
  const d = new Date(ms)
  return d.toLocaleTimeString(activeLocale(), { hour: 'numeric', minute: '2-digit' })
}

function fmtDayHeader(ts, t) {
  const ms = tsToMs(ts)
  if (!ms) return ''
  const d = new Date(ms)
  const today = new Date()
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString())     return t ? t('chat.today') : 'Today'
  if (d.toDateString() === yesterday.toDateString()) return t ? t('chat.yesterday') : 'Yesterday'
  const loc = activeLocale()
  if (today - d < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString(loc, { weekday: 'long' })
  }
  return d.toLocaleDateString(loc, { month: 'short', day: 'numeric', year: 'numeric' })
}

function dayKey(ts) {
  const ms = tsToMs(ts)
  if (!ms) return 'unknown'
  return new Date(ms).toDateString()
}

// Format byte count into a friendly "1.2 MB" / "456 KB" string for
// the PDF card. Returns '' for null/undefined so the dot separator
// can be rendered conditionally.
function fmtBytes(n) {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/* ---------- Subcomponents ----------------------------------- */

function ChatAvatar({ chat, size = 'lg' }) {
  const isTeam = chat?.type === 'team_announcement'
  const cls = size === 'lg' ? 'h-10 w-10 rounded-2xl' : 'h-8 w-8 rounded-xl'

  if (chat?.photoUrl) {
    return (
      <img
        src={chat.photoUrl}
        alt=""
        className={`${cls} object-cover ring-1 ring-zinc-200 flex-shrink-0`}
      />
    )
  }
  if (isTeam) {
    return (
      <div className={`${cls} bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0`}>
        <MegaphoneIcon className={size === 'lg' ? 'h-5 w-5 text-[#5a8a1f]' : 'h-4 w-4 text-[#5a8a1f]'} />
      </div>
    )
  }
  return (
    <div className={`${cls} bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center flex-shrink-0`}>
      <UserIcon className={size === 'lg' ? 'h-5 w-5 text-zinc-500' : 'h-4 w-4 text-zinc-500'} />
    </div>
  )
}

function SystemMessage({ message }) {
  return (
    <div className="flex justify-center my-3">
      <p className="text-[11px] text-zinc-400 italic px-3 py-1 bg-zinc-100 rounded-full">
        {message.text}
      </p>
    </div>
  )
}

// Render message text with auto-detected http/https URLs as clickable
// links. Coaches frequently paste links to practice plans, registration
// forms, league sites, etc.; rendering them as plain text (the previous
// behavior) forced parents to copy/paste into a browser. The regex
// catches well-formed http(s) URLs only — bare "example.com" without a
// scheme stays plain text, since auto-linking ambiguous strings causes
// more false positives than wins. break-all on the anchor handles long
// URLs gracefully inside narrow chat bubbles.
function renderTextWithLinks(text) {
  if (!text) return null
  const URL_RE = /https?:\/\/[^\s<>"']+/g
  const out = []
  let lastIndex = 0
  let match
  let key = 0
  while ((match = URL_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index))
    }
    // Strip common trailing punctuation that's almost certainly NOT
    // part of the URL ("see https://example.com." → trailing period
    // shouldn't be in the link).
    let url = match[0]
    let trailing = ''
    while (/[.,!?;:)\]]$/.test(url)) {
      trailing = url.slice(-1) + trailing
      url = url.slice(0, -1)
    }
    out.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline break-all hover:no-underline"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    )
    if (trailing) out.push(trailing)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex))
  return out
}

function MessageBubble({ message, isMine, showSender, members }) {
  const { t } = useTranslation()
  if (message.type === 'system') return <SystemMessage message={message} />

  const isDeleted = !!message.deletedAt
  const isImage = message.type === 'image' && message.attachment?.url
  // Accept either `message.type === 'pdf'` or the explicit
  // `attachment.type === 'pdf'` discriminator. The latter is the
  // canonical source of truth for the renderer; the former exists
  // for inbox previews.
  const isPdf = (message.type === 'pdf' || message.attachment?.type === 'pdf')
    && message.attachment?.url

  // Use denormalized senderName from the message; fall back to
  // members map if it's missing (older messages, edge cases).
  const senderName = message.senderName ||
    members?.[message.senderId]?.displayName ||
    t('chat.memberFallback')
  const senderPhoto = message.senderPhotoURL ||
    members?.[message.senderId]?.photoURL || null

  return (
    <div className={['flex gap-2 px-2', isMine ? 'flex-row-reverse' : 'flex-row'].join(' ')}>
      {/* Avatar (only on first message of run from this sender) */}
      <div className="w-8 flex-shrink-0">
        {showSender && !isMine && (
          senderPhoto ? (
            <img src={senderPhoto} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-zinc-200" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center">
              <span className="text-[11px] font-bold text-zinc-500 uppercase">
                {senderName[0]}
              </span>
            </div>
          )
        )}
      </div>

      {/* Bubble column */}
      <div className={['flex-1 min-w-0 max-w-[75%]', isMine ? 'items-end' : 'items-start', 'flex flex-col'].join(' ')}>
        {showSender && !isMine && (
          <p className="text-[11px] font-bold text-[#5a8a1f] mb-0.5 px-1" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.3px' }}>
            {senderName.toUpperCase()}
          </p>
        )}

        <div
          className={[
            'rounded-2xl px-3.5 py-2 shadow-sm',
            isMine
              ? 'bg-[#b5f03d] text-[#0d1f14] rounded-br-md'
              : 'bg-white text-[#0d1f14] ring-1 ring-zinc-200 rounded-bl-md',
            isDeleted ? 'opacity-60 italic' : '',
          ].join(' ')}
        >
          {isDeleted ? (
            <p className="text-sm">{t('chat.messageDeleted')}</p>
          ) : (
            <>
              {isImage && (
                <a
                  href={message.attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mb-1 -mx-1.5 -mt-1"
                >
                  <img
                    src={message.attachment.thumbnailUrl || message.attachment.url}
                    alt={message.attachment.name || t('chat.attachmentAlt')}
                    className="rounded-xl max-w-full max-h-72 object-cover"
                    loading="lazy"
                  />
                </a>
              )}
              {isPdf && (
                // Tappable PDF card: opens the PDF in a new tab.
                // Lime-on-pitch for "mine", pitch-on-white for others
                // so it sits inside either bubble color cleanly.
                <a
                  href={message.attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={[
                    'flex items-center gap-2.5 rounded-xl px-3 py-2 mb-1 transition-colors',
                    isMine
                      ? 'bg-[#0d1f14]/10 hover:bg-[#0d1f14]/15'
                      : 'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 hover:bg-[#b5f03d]/25',
                  ].join(' ')}
                >
                  <DocumentTextIcon
                    className={[
                      'h-7 w-7 flex-shrink-0',
                      isMine ? 'text-[#0d1f14]' : 'text-[#5a8a1f]',
                    ].join(' ')}
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-[#0d1f14] truncate">
                      {message.attachment.fileName || message.attachment.name || t('chat.pdfDefaultName')}
                    </p>
                    <p className="text-[10px] text-[#0d1f14]/60">
                      {t('chat.pdfLabel')}
                      {(message.attachment.sizeBytes || message.attachment.size) ? (
                        <> &middot; {fmtBytes(message.attachment.sizeBytes || message.attachment.size)}</>
                      ) : null}
                    </p>
                  </div>
                </a>
              )}
              {message.text && (
                <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                  {renderTextWithLinks(message.text)}
                </p>
              )}
            </>
          )}
        </div>

        <p className={['text-[10px] text-zinc-400 mt-0.5 px-1', isMine ? 'text-right' : 'text-left'].join(' ')}>
          {fmtTime(message.sentAt)}
          {message.edited && !isDeleted && (
            <span className="inline-flex items-center gap-0.5 ml-1.5">
              <PencilIcon className="h-2.5 w-2.5" /> {t('chat.edited')}
            </span>
          )}
        </p>
      </div>
    </div>
  )
}

function DayDivider({ ts }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3 my-4 px-2">
      <div className="flex-grow h-px bg-zinc-200" />
      <span
        className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#5a8a1f]"
        style={{ fontFamily: HEAD_FONT }}
      >
        {fmtDayHeader(ts, t)}
      </span>
      <div className="flex-grow h-px bg-zinc-200" />
    </div>
  )
}

function EmptyChat({ chat }) {
  const { t } = useTranslation()
  const isTeam = chat?.type === 'team_announcement'
  return (
    <div className="flex-1 flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-5">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-[#5a8a1f]" />
        </div>
        <h3
          className="text-lg font-bold uppercase text-[#0d1f14] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          {isTeam ? t('chat.emptyAnnouncementsTitle') : t('chat.emptyMessagesTitle')}
        </h3>
        <p className="text-sm text-zinc-500">
          {isTeam
            ? t('chat.emptyAnnouncementsBody')
            : t('chat.emptyMessagesBody')}
        </p>
      </div>
    </div>
  )
}

function NotFoundChat() {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-6">
      <div className="text-center">
        <ChatBubbleLeftRightIcon className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
        <h2
          className="text-xl font-bold uppercase text-[#0d1f14] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          {t('chat.notFound')}
        </h2>
        <p className="text-sm text-zinc-500 mb-5">
          {t('chat.notFoundBody')}
        </p>
        <Link
          to="/messages"
          className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase transition-all active:scale-[0.97]"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('chat.backToMessagesButton')}
        </Link>
      </div>
    </div>
  )
}

/* ---------- Main page --------------------------------------- */

export default function ChatRoomV2() {
  const { chatId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { firebaseUser, userProfile } = useAuth()
  const uid = firebaseUser?.uid || null

  // Denormalize current user info for outgoing messages.
  // Each fallback layer is one step less specific so missing
  // userProfile fields don't end up sending "undefined" as a name.
  const myDisplayName = useMemo(() => (
    userProfile?.displayName ||
    [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ').trim() ||
    firebaseUser?.displayName ||
    (firebaseUser?.email ? firebaseUser.email.split('@')[0] : t('chat.memberFallback'))
  ), [userProfile, firebaseUser, t])
  const myPhotoURL = userProfile?.photoURL || firebaseUser?.photoURL || null

  const { chat, loading: chatLoading, error: chatError } = useChat(chatId)
  const { messages, loading: msgsLoading } = useChatMessages(chatId)
  const { teamRole } = useRole()

  const isTeam = chat?.type === 'team_announcement'
  const memberCount = useMemo(() => (
    chat?.participantIds?.length || 0
  ), [chat?.participantIds])
  const myRole = chat?.members?.[uid]?.role

  // Fetch the team's chatMode once when the chat loads. We don't need
  // a live listener — the coach changing this in EditTeam is rare and
  // the parent can refresh if they suspect it changed. Single read,
  // no subscription overhead. Default 'open' if missing.
  const [teamChatMode, setTeamChatMode] = useState('open')
  useEffect(() => {
    if (!isTeam || !chat?.teamId) {
      setTeamChatMode('open')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'teams', chat.teamId))
        if (cancelled) return
        const mode = snap.exists() && snap.data()?.chatMode === 'broadcast'
          ? 'broadcast'
          : 'open'
        setTeamChatMode(mode)
      } catch (err) {
        console.warn('[ChatRoomV2] team chatMode fetch failed:', err?.code, err?.message)
        if (!cancelled) setTeamChatMode('open')
      }
    })()
    return () => { cancelled = true }
  }, [isTeam, chat?.teamId])

  // Posting permission. Admins (coaches/staff) ALWAYS post. For team
  // announcement chats, parents post only when chatMode === 'open'.
  // DM participants post freely.
  const myTeamRole = isTeam && chat?.teamId ? teamRole(chat.teamId) : null
  const isParentOnTeam = myTeamRole === 'parent'
  const isBroadcastReadOnly = isTeam && teamChatMode === 'broadcast' && isParentOnTeam
  const canPost =
    myRole === 'admin' ||
    (chat?.type === 'dm' && chat?.participantIds?.includes(uid)) ||
    // Open-mode team chat: any participant (including parents) can post.
    // Skipped when broadcast-only is on for this team.
    (isTeam && teamChatMode === 'open' && chat?.participantIds?.includes(uid) && !isBroadcastReadOnly)

  // For DMs, the title + avatar should reflect the OTHER person
  // from this user's POV. Stored chat.name = creator-perspective,
  // so we always derive dynamically here.
  const headerName = useMemo(() => {
    if (chat?.type === 'dm' && uid) {
      const otherUid = (chat.participantIds || []).find((p) => p !== uid)
      const m = chat.members?.[otherUid]
      if (m?.displayName) return m.displayName
    }
    return chat?.name || t('chat.untitled')
  }, [chat, uid, t])
  const headerChat = useMemo(() => {
    if (chat?.type === 'dm' && uid) {
      const otherUid = (chat.participantIds || []).find((p) => p !== uid)
      const m = chat.members?.[otherUid]
      if (m) return { ...chat, photoUrl: m.photoURL || chat.photoUrl }
    }
    return chat
  }, [chat, uid])

  // Auto-scroll: keep the message list pinned to the bottom unless
  // the user has scrolled up to read older messages. We track that
  // with a ref so re-renders don't fight it.
  const scrollRef = useRef(null)
  const wasNearBottomRef = useRef(true)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (wasNearBottomRef.current) {
      // Use rAF so the new message has actually rendered
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
  }, [messages.length])

  const handleScroll = (e) => {
    const el = e.currentTarget
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
    wasNearBottomRef.current = nearBottom
  }

  // Auto mark-read: on chat open + whenever a new message arrives
  // while the user has the chat open (since they're seeing it).
  useEffect(() => {
    if (!chatId || !uid || messages.length === 0) return
    const lastMsg = messages[messages.length - 1]
    if (!lastMsg) return
    markChatRead(chatId, uid, lastMsg.id)
  }, [chatId, uid, messages.length])

  // Mark read on initial mount even if there are no messages, so a
  // fresh chat doesn't keep the unread dot lit forever. (No-op if
  // already marked.)
  useEffect(() => {
    if (chatId && uid) markChatRead(chatId, uid, null)
  }, [chatId, uid])

  // Chat loading / not found / error states
  if (chatError === 'not-found') return <NotFoundChat />
  if (chatLoading || !chat) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5]">
        <ArrowPathIcon className="h-6 w-6 text-zinc-400 animate-spin" />
      </div>
    )
  }

  // Group consecutive messages from the same sender within 5 minutes
  // so we only show the avatar/name once per "burst." Also insert
  // day-divider headers between days.
  const grouped = []
  let prev = null
  let prevDayKey = null
  for (const m of messages) {
    const k = dayKey(m.sentAt)
    if (k !== prevDayKey) {
      grouped.push({ kind: 'day-divider', ts: m.sentAt, key: `day-${k}` })
      prevDayKey = k
      prev = null
    }
    const sameSender = prev && prev.senderId === m.senderId
    const within5min = prev && (tsToMs(m.sentAt) - tsToMs(prev.sentAt)) < 5 * 60 * 1000
    const showSender = !sameSender || !within5min
    grouped.push({ kind: 'message', message: m, showSender, key: m.id })
    prev = m
  }

  // Team-context nav. Only shown for team_announcement chats since
  // they're scoped to a single team. DMs and other chat types don't
  // have a teamId on the chat doc, so the nav wouldn't know what to
  // show. Renders inline (not sticky) above the chat header so the
  // sticky chat header still works as expected when scrolling.
  const teamChatId = chat?.type === 'team_announcement' ? chat.teamId : null;

  return (
    <div className="min-h-svh flex flex-col bg-[#f7faf5]">
      {teamChatId && (
        <div className="px-4 sm:px-6 lg:px-8 pt-1">
          <TeamSubNav teamId={teamChatId} />
        </div>
      )}
      {/* Sticky header */}
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-3 sm:px-4 py-2.5 flex items-center gap-3">
        {/* Context-aware back navigation. For team announcement chats,
            back returns to the TEAM page (which keeps TeamSubNav visible
            so the user doesn't lose the team context). For DMs and other
            chat types, back goes to the cross-team inbox where TeamSubNav
            doesn't apply anyway. Pre-fix this always navigated to
            /messages, dropping the team nav for users who entered chat
            via the team's Chat tab. */}
        <button
          type="button"
          onClick={() => {
            if (teamChatId) {
              navigate(`/teams/${teamChatId}`);
            } else {
              navigate('/messages');
            }
          }}
          aria-label={teamChatId ? t('chat.backToTeam') : t('chat.backToMessages')}
          className="p-2 -ml-1 rounded-lg text-zinc-500 hover:text-[#0d1f14] hover:bg-zinc-100 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>

        <ChatAvatar chat={headerChat} size="lg" />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0d1f14] truncate flex items-center gap-2">
            {headerName}
            {isTeam && (
              <span
                className="inline-flex items-center gap-0.5 rounded bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30 text-[#5a8a1f] px-1.5 py-px text-[9px] font-bold uppercase"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {t('chat.teamBadge')}
              </span>
            )}
          </p>
          <p className="text-[11px] text-zinc-500">
            {isTeam
              ? (memberCount === 1
                ? t('chat.memberCountOne', { count: memberCount })
                : t('chat.memberCountOther', { count: memberCount }))
              : t('chat.directMessage')}
          </p>
        </div>
      </header>

      {/* Read-only notice for parents in a team announcement channel.
          Without this, parents land on the channel, see the empty
          composer area replaced by the "Message Coach" buttons, and
          aren't sure why they can't type — they may not realize this
          is a broadcast surface. The banner is intentionally low-key
          (small zinc text on a subtle bg) so it explains without
          shouting. Coach-side viewers (canPost === true) skip this. */}
      {isTeam && !canPost && (
        <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-2">
          <p className="text-[11px] text-zinc-500 text-center max-w-2xl mx-auto leading-snug">
            {isBroadcastReadOnly
              ? t('chat.broadcastOnlyNotice')
              : t('chat.announcementChannelNotice')}
          </p>
        </div>
      )}

      {/* Message list */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto py-4"
      >
        {msgsLoading ? (
          <div className="flex items-center justify-center py-20">
            <ArrowPathIcon className="h-6 w-6 text-zinc-400 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <EmptyChat chat={chat} />
        ) : (
          <div className="space-y-1.5 max-w-2xl mx-auto px-2">
            {grouped.map((g) => g.kind === 'day-divider' ? (
              <DayDivider key={g.key} ts={g.ts} />
            ) : (
              <MessageBubble
                key={g.key}
                message={g.message}
                isMine={g.message.senderId === uid}
                showSender={g.showSender}
                members={chat.members}
              />
            ))}
          </div>
        )}
      </div>

      {/* Composer / read-only state.
          - canPost: real composer (text + image). When this is a team
            chat, also pass chatMode so the placeholder reflects whether
            it's an open group chat or an announcement broadcast.
          - team announcement (read-only members): a calm lime/pitch
            broadcast banner (only when chatMode === 'broadcast') above
            the MessageCoachBar one-tap DM buttons for each coach.
            Beats the old "go to your inbox" hint which sent users on
            a 4-click journey through navigation they didn't know
            existed.
          - other read-only states: minimal zinc notice */}
      {canPost ? (
        <ChatComposerV2
          chatId={chatId}
          senderId={uid}
          senderName={myDisplayName}
          senderPhotoURL={myPhotoURL}
          placeholder={isTeam
            ? (teamChatMode === 'broadcast' ? t('chat.composerPlaceholderAnnouncement') : t('chat.composerPlaceholderTeam'))
            : t('chat.composerPlaceholder')}
          chatType={chat?.type}
          chatMode={teamChatMode}
          teamRoleOnChat={myTeamRole}
        />
      ) : isTeam ? (
        <>
          {isBroadcastReadOnly && (
            // Calm lime/pitch banner explaining why the composer is
            // hidden. Sits ABOVE the MessageCoachBar so parents have
            // both the "why" (this banner) and the "what next" (the
            // one-tap coach DM buttons) without scrolling.
            <div className="bg-white border-t border-zinc-200 px-3 sm:px-4 pt-3 sticky bottom-0 z-10">
              <div className="max-w-2xl mx-auto flex items-start gap-2.5 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 px-3.5 py-2.5">
                <SpeakerWaveIcon className="h-4 w-4 text-[#5a8a1f] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#0d1f14] leading-snug">
                  {t('chat.broadcastOnlyNotice')}
                </p>
              </div>
            </div>
          )}
          <MessageCoachBar
            chat={chat}
            me={{ uid, displayName: myDisplayName, photoURL: myPhotoURL }}
          />
        </>
      ) : (
        <div className="bg-white border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+64px+env(safe-area-inset-bottom))] md:pb-3 sticky bottom-0">
          <div className="max-w-2xl mx-auto rounded-xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-center">
            <p className="text-xs text-zinc-500">
              {t('chat.cannotPost')}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
